import { useState } from "react";
import { useCClassTrib } from "@/features/fiscal/hooks/useFiscal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { SacFiscalSyncButton } from "@/components/SacFiscalSyncButton";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import type { CClassTribListagemDto, CClassTribDto, SalvarCClassTribRequest } from "@/features/fiscal/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

const TIPO_ALIQUOTA_OPTIONS = [
  { value: 1, label: "Alíquota Padrão" },
  { value: 2, label: "Redução de Alíquota" },
  { value: 3, label: "Crédito Presumido" },
  { value: 4, label: "Monofásico" },
  { value: 5, label: "Isento / Não Tributado" },
];

const EMPTY_FORM: SalvarCClassTribRequest = {
  codCClassTrib: "", nome: "", descricao: "", lcRedacao: "", leiDecreto: "",
  tipoAliquota: 1, perRedIbs: 0, perRedCbs: 0,
  indTribRegular: false, indCreditoPresumido: false, indMonofasico: false,
  indMonoRetencao: false, indMonoRetido: false, indMonoDiferido: false, indEstornoCredito: false,
  indNFe: true, indNFCe: false, indCTe: false, indCTeOS: false,
  indBPe: false, indNF3e: false, indNFCom: false, indNFSe: false,
  dtInicioVigencia: null, dtFimVigencia: null,
  anexo: "", linkLei: "", creditoPara: "",
};

function dtoToForm(d: CClassTribDto): SalvarCClassTribRequest {
  return {
    codCClassTrib: d.codCClassTrib, nome: d.nome, descricao: d.descricao ?? "",
    lcRedacao: d.lcRedacao ?? "", leiDecreto: d.leiDecreto ?? "",
    tipoAliquota: d.tipoAliquota, perRedIbs: d.perRedIbs, perRedCbs: d.perRedCbs,
    indTribRegular: d.indTribRegular, indCreditoPresumido: d.indCreditoPresumido,
    indMonofasico: d.indMonofasico, indMonoRetencao: d.indMonoRetencao,
    indMonoRetido: d.indMonoRetido, indMonoDiferido: d.indMonoDiferido,
    indEstornoCredito: d.indEstornoCredito,
    indNFe: d.indNFe, indNFCe: d.indNFCe, indCTe: d.indCTe, indCTeOS: d.indCTeOS,
    indBPe: d.indBPe, indNF3e: d.indNF3e, indNFCom: d.indNFCom, indNFSe: d.indNFSe,
    dtInicioVigencia: d.dtInicioVigencia ? d.dtInicioVigencia.substring(0, 10) : null,
    dtFimVigencia:    d.dtFimVigencia    ? d.dtFimVigencia.substring(0, 10)    : null,
    anexo: d.anexo ?? "", linkLei: d.linkLei ?? "", creditoPara: d.creditoPara ?? "",
  };
}

export default function CClassTrib() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const { query, obterPorId, criar, atualizar, excluir } = useCClassTrib(busca || undefined, pagina, tamanhoPagina);
  const itens: CClassTribListagemDto[] = query.data?.itens ?? [];

  const totalItens = query.data?.total ?? 0;
  const primeiroItem = totalItens === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, totalItens);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [excluirId,  setExcluirId]  = useState<number | null>(null);
  const [editingId,  setEditingId]  = useState<number | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [form,       setForm]       = useState<SalvarCClassTribRequest>(EMPTY_FORM);

  const set = <K extends keyof SalvarCClassTribRequest>(k: K, v: SalvarCClassTribRequest[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    setLoading(true);
    try { const dto = await obterPorId(id); setEditingId(id); setForm(dtoToForm(dto)); setDialogOpen(true); }
    catch { toast.error("Não foi possível carregar a classificação tributária."); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.codCClassTrib.trim()) { toast.error("Código CClass é obrigatório."); return; }
    if (!form.nome.trim()) { toast.error("Nome é obrigatório."); return; }
    try {
      if (editingId) {
        await atualizar.mutateAsync({ id: editingId, req: form });
        toast.success("Classificação tributária atualizada.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Classificação tributária criada.");
      }
      setDialogOpen(false);
    } catch { /* global toast */ }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    try { await excluir.mutateAsync(excluirId); toast.success("Excluída com sucesso."); }
    catch { /* global toast */ }
    setExcluirId(null);
  };

  const isSaving = criar.isPending || atualizar.isPending;
  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6" /> Classificação Tributária (IBS/CBS)
          </h1>
          <p className="text-muted-foreground">CClass Tributária — Reforma Tributária IBS/CBS</p>
        </div>
        <div className="flex gap-2">
          {podeEditar && (
            <SacFiscalSyncButton
              endpoint="/api/cadastros/fiscal/sacfiscal/sincronizar/cclasstrib"
              onDone={() => query.refetch()}
            />
          )}
          {podeEditar && (
            <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nova</Button>
          )}
        </div>
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por código ou nome..." className="pl-9" value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[100px]">Código</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead className="text-right w-[100px]">% Red. IBS</TableHead>
              <TableHead className="text-right w-[100px]">% Red. CBS</TableHead>
              <TableHead className="w-[120px]">Início Vigência</TableHead>
              <TableHead className="w-[90px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {itens.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-mono font-semibold">{c.codCClassTrib}</TableCell>
                <TableCell>{c.nome}</TableCell>
                <TableCell className="text-right">{c.perRedIbs > 0 ? `${c.perRedIbs}%` : "—"}</TableCell>
                <TableCell className="text-right">{c.perRedCbs > 0 ? `${c.perRedCbs}%` : "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {c.dtInicioVigencia ? new Date(c.dtInicioVigencia).toLocaleDateString("pt-BR") : "—"}
                </TableCell>
                <TableCell>
                  {podeEditar && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c.id)} disabled={loading}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setExcluirId(c.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {itens.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Nenhuma classificação tributária cadastrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={query.data.totalPaginas}
          totalItens={totalItens}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={setTamanhoPagina}
        />
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Classificação Tributária" : "Nova Classificação Tributária"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="dados">
              <TabsList className="mb-4">
                <TabsTrigger value="dados">Dados</TabsTrigger>
                <TabsTrigger value="indicadores">Indicadores</TabsTrigger>
                <TabsTrigger value="documentos">Documentos</TabsTrigger>
              </TabsList>

              {/* ── Dados ─────────────────────────────────────────── */}
              <TabsContent value="dados" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="codCClassTrib">Código CClass *</Label>
                    <Input id="codCClassTrib" maxLength={8} value={form.codCClassTrib} onChange={e => set("codCClassTrib", e.target.value)} className="font-mono" required />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="nome">Nome *</Label>
                    <Input id="nome" maxLength={255} value={form.nome} onChange={e => set("nome", e.target.value)} required />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea id="descricao" rows={3} value={form.descricao} onChange={e => set("descricao", e.target.value)} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Tipo de Alíquota</Label>
                    <Select value={String(form.tipoAliquota)} onValueChange={v => set("tipoAliquota", parseInt(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPO_ALIQUOTA_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="perRedIbs">% Redução IBS</Label>
                    <Input id="perRedIbs" type="number" min={0} max={100} step={0.01} value={form.perRedIbs} onChange={e => set("perRedIbs", parseFloat(e.target.value) || 0)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="perRedCbs">% Redução CBS</Label>
                    <Input id="perRedCbs" type="number" min={0} max={100} step={0.01} value={form.perRedCbs} onChange={e => set("perRedCbs", parseFloat(e.target.value) || 0)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="dtInicioVigencia">Início Vigência</Label>
                    <Input id="dtInicioVigencia" type="date" value={form.dtInicioVigencia ?? ""} onChange={e => set("dtInicioVigencia", e.target.value || null)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dtFimVigencia">Fim Vigência</Label>
                    <Input id="dtFimVigencia" type="date" value={form.dtFimVigencia ?? ""} onChange={e => set("dtFimVigencia", e.target.value || null)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="leiDecreto">Lei/Decreto</Label>
                    <Input id="leiDecreto" maxLength={60} value={form.leiDecreto} onChange={e => set("leiDecreto", e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="anexo">Anexo</Label>
                    <Input id="anexo" maxLength={60} value={form.anexo} onChange={e => set("anexo", e.target.value)} />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="linkLei">Link da Lei</Label>
                  <Input id="linkLei" maxLength={120} value={form.linkLei} onChange={e => set("linkLei", e.target.value)} placeholder="https://" />
                </div>
              </TabsContent>

              {/* ── Indicadores ───────────────────────────────────── */}
              <TabsContent value="indicadores" className="space-y-3">
                {[
                  { id: "indTribRegular",      label: "Tributação Regular",    field: "indTribRegular"      as const },
                  { id: "indCreditoPresumido", label: "Crédito Presumido",     field: "indCreditoPresumido" as const },
                  { id: "indMonofasico",       label: "Monofásico",            field: "indMonofasico"       as const },
                  { id: "indMonoRetencao",     label: "Mono Retenção",         field: "indMonoRetencao"     as const },
                  { id: "indMonoRetido",       label: "Mono Retido",           field: "indMonoRetido"       as const },
                  { id: "indMonoDiferido",     label: "Mono Diferido",         field: "indMonoDiferido"     as const },
                  { id: "indEstornoCredito",   label: "Estorno de Crédito",    field: "indEstornoCredito"   as const },
                ].map(({ id, label, field }) => (
                  <div key={id} className="flex items-center gap-3">
                    <Switch id={id} checked={form[field] as boolean} onCheckedChange={v => set(field, v)} />
                    <Label htmlFor={id}>{label}</Label>
                  </div>
                ))}
              </TabsContent>

              {/* ── Documentos Fiscais ─────────────────────────────── */}
              <TabsContent value="documentos" className="space-y-3">
                <p className="text-sm text-muted-foreground">Documentos fiscais habilitados para esta classificação:</p>
                {[
                  { id: "indNFe",   label: "NF-e (Nota Fiscal Eletrônica)",  field: "indNFe"   as const },
                  { id: "indNFCe",  label: "NFC-e (Nota Consumidor)",         field: "indNFCe"  as const },
                  { id: "indCTe",   label: "CT-e (Conhecimento de Transporte)", field: "indCTe" as const },
                  { id: "indCTeOS", label: "CT-e OS",                         field: "indCTeOS" as const },
                  { id: "indBPe",   label: "BP-e (Bilhete de Passagem)",      field: "indBPe"   as const },
                  { id: "indNF3e",  label: "NF3-e (Energia Elétrica)",        field: "indNF3e"  as const },
                  { id: "indNFCom", label: "NFCom",                           field: "indNFCom" as const },
                  { id: "indNFSe",  label: "NFS-e (Nota Fiscal de Serviço)",  field: "indNFSe"  as const },
                ].map(({ id, label, field }) => (
                  <div key={id} className="flex items-center gap-3">
                    <Switch id={id} checked={form[field] as boolean} onCheckedChange={v => set(field, v)} />
                    <Label htmlFor={id}>{label}</Label>
                  </div>
                ))}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>{isSaving ? "Salvando..." : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluirId} onOpenChange={() => setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir classificação tributária</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleExcluir} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
